import { Link } from 'react-router-dom';
import { loadModePref, saveModePref } from '../game/modePref';
import { modeLabel, type GameMode } from '@shared/chart';
import { TrademarkDisclaimer } from './TrademarkDisclaimer';
import './modePicker.css';

interface ModeCardProps {
  mode: GameMode;
  title: string;
  tagline: string;
  description: string;
  controls: string;
  badge?: string;
  accentVar: string;
  to: string;
}

/**
 * One of the mode cards on the landing page. Each commits to a
 * distinct accent so the palette previews how each mode actually
 * looks in-game (magenta for the osu-style Circles ring tap, lime
 * for Piano's falling-tile waterfall, amber for Guitar's neon
 * fretboard, cyan for Pulse's grid runner).
 */
function ModeCard({
  mode,
  title,
  tagline,
  description,
  controls,
  badge,
  accentVar,
  to,
}: ModeCardProps) {
  return (
    <Link
      to={to}
      className="mode-card"
      data-testid={`mode-card-${mode}`}
      style={{ '--mode-accent': accentVar } as React.CSSProperties}
      onClick={() => saveModePref(mode)}
    >
      {badge && <span className="mode-card-badge">{badge}</span>}
      <div className="mode-card-art" aria-hidden="true">
        {mode === 'pulse' ? (
          <PulseArt />
        ) : mode === 'circles' ? (
          <CirclesArt />
        ) : mode === 'piano' ? (
          <PianoArt />
        ) : mode === 'guitar' ? (
          <GuitarArt />
        ) : mode === 'waves' ? (
          <WavesArt />
        ) : (
          <JumpArt />
        )}
      </div>
      <div className="mode-card-body">
        <h2 className="mode-card-title">{title}</h2>
        <p className="mode-card-tagline">{tagline}</p>
        <p className="mode-card-description">{description}</p>
        <p className="mode-card-controls">
          <strong>Controls:</strong> {controls}
        </p>
        <span className="mode-card-cta">
          Enter {title} <span aria-hidden="true">→</span>
        </span>
      </div>
    </Link>
  );
}

/**
 * Tiny SVG glyphs preview each mode's signature visual — the Pulse
 * runner sliding between two rails, and the Circles ring with cursor
 * approaching. Drawn in CSS-coloured currentColor so they pick up the
 * card's accent on hover.
 */
function PulseArt() {
  return (
    <svg viewBox="0 0 120 60" className="mode-art-svg">
      <line x1="0" y1="14" x2="120" y2="14" stroke="currentColor" strokeWidth="2" opacity="0.5" />
      <line x1="0" y1="46" x2="120" y2="46" stroke="currentColor" strokeWidth="2" opacity="0.5" />
      <rect x="36" y="40" width="14" height="6" rx="2" fill="currentColor" opacity="0.7" />
      <rect x="78" y="8" width="14" height="6" rx="2" fill="currentColor" opacity="0.7" />
      <circle cx="22" cy="42" r="5" fill="currentColor" />
      <path
        d="M 22 42 Q 35 8, 60 30 T 100 30"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="3 3"
        opacity="0.6"
      />
    </svg>
  );
}

function CirclesArt() {
  return (
    <svg viewBox="0 0 120 60" className="mode-art-svg">
      <circle cx="32" cy="30" r="14" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.4" />
      <circle cx="32" cy="30" r="9" fill="currentColor" opacity="0.85" />
      <circle cx="64" cy="30" r="20" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.6" />
      <circle cx="64" cy="30" r="9" fill="currentColor" opacity="0.5" />
      <circle cx="96" cy="30" r="26" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.3" />
      <circle cx="96" cy="30" r="9" fill="currentColor" opacity="0.25" />
    </svg>
  );
}

function PianoArt() {
  // Four lanes; tiles staggered by lane to suggest a falling rhythm.
  return (
    <svg viewBox="0 0 120 60" className="mode-art-svg">
      <line x1="0" y1="48" x2="120" y2="48" stroke="currentColor" strokeWidth="2" opacity="0.7" />
      {[0, 1, 2, 3].map((lane) => {
        const x = 12 + lane * 28;
        return (
          <line
            key={`lane-${lane}`}
            x1={x + 12}
            y1="0"
            x2={x + 12}
            y2="60"
            stroke="currentColor"
            strokeWidth="1"
            opacity="0.15"
          />
        );
      })}
      <rect x="12" y="36" width="20" height="10" rx="2" fill="currentColor" opacity="0.85" />
      <rect x="40" y="14" width="20" height="10" rx="2" fill="currentColor" opacity="0.7" />
      <rect x="68" y="22" width="20" height="10" rx="2" fill="currentColor" opacity="0.55" />
      <rect x="96" y="4" width="20" height="10" rx="2" fill="currentColor" opacity="0.4" />
    </svg>
  );
}

/**
 * Fretboard glyph: trapezoidal lanes diverging upward (perspective),
 * a strum bar near the bottom, and four coloured gems descending
 * staggered along each lane. Conveys the perspective-highway look at
 * a glance and visually separates this card from the parallel-lane
 * Piano glyph above.
 */
function GuitarArt() {
  // Lane vertices form a trapezoid: narrow at the top (~y=4),
  // widening to the strum bar (~y=52). Each lane uses its
  // GH-classic colour rather than `currentColor` so the four lanes
  // remain visually distinct in the muted "off" state of the card.
  const LANE_COLOURS = ['#3bff9d', '#ff5577', '#ffd84d', '#4dc8ff'];
  const TOP_Y = 4;
  const BOT_Y = 52;
  const TOP_HALF_WIDTH = 18;
  const BOT_HALF_WIDTH = 50;
  const CENTRE_X = 60;
  return (
    <svg viewBox="0 0 120 60" className="mode-art-svg">
      {LANE_COLOURS.map((colour, lane) => {
        const t1 = lane / 4;
        const t2 = (lane + 1) / 4;
        const xTopLeft = CENTRE_X + (t1 * 2 - 1) * TOP_HALF_WIDTH;
        const xTopRight = CENTRE_X + (t2 * 2 - 1) * TOP_HALF_WIDTH;
        const xBotLeft = CENTRE_X + (t1 * 2 - 1) * BOT_HALF_WIDTH;
        const xBotRight = CENTRE_X + (t2 * 2 - 1) * BOT_HALF_WIDTH;
        return (
          <polygon
            key={`lane-${lane}`}
            points={`${xTopLeft},${TOP_Y} ${xTopRight},${TOP_Y} ${xBotRight},${BOT_Y} ${xBotLeft},${BOT_Y}`}
            fill={colour}
            fillOpacity={0.15}
            stroke={colour}
            strokeOpacity={0.55}
            strokeWidth={1}
          />
        );
      })}
      <line
        x1={CENTRE_X - BOT_HALF_WIDTH}
        y1={BOT_Y}
        x2={CENTRE_X + BOT_HALF_WIDTH}
        y2={BOT_Y}
        stroke="currentColor"
        strokeWidth="2"
        opacity="0.85"
      />
      {LANE_COLOURS.map((colour, lane) => {
        // Stagger gems at different y heights so the lanes read as
        // four notes in flight rather than a single chord.
        const gemY = 16 + lane * 7;
        const t = (lane + 0.5) / 4;
        const cx = CENTRE_X + (t * 2 - 1) * (TOP_HALF_WIDTH + ((gemY - TOP_Y) / (BOT_Y - TOP_Y)) * (BOT_HALF_WIDTH - TOP_HALF_WIDTH));
        return (
          <circle
            key={`gem-${lane}`}
            cx={cx}
            cy={gemY}
            r={4 + lane * 0.6}
            fill={colour}
            stroke="rgba(255,255,255,0.65)"
            strokeWidth={0.6}
          />
        );
      })}
    </svg>
  );
}

/**
 * Side-scrolling corridor glyph: ceiling/floor walls, a couple of
 * spike obstacles, and a triangle player tracing a piecewise-linear
 * trajectory through the gap. Conveys the spacewaves.io / GD-wave
 * silhouette at a glance — clearly distinct from the perspective
 * fretboard and the falling-tile waterfall.
 */
function WavesArt() {
  return (
    <svg viewBox="0 0 120 60" className="mode-art-svg">
      <line x1="0" y1="6" x2="120" y2="6" stroke="currentColor" strokeWidth="2" opacity="0.55" />
      <line x1="0" y1="54" x2="120" y2="54" stroke="currentColor" strokeWidth="2" opacity="0.55" />
      <polygon points="34,6 42,18 26,18" fill="currentColor" opacity="0.85" />
      <polygon points="74,54 82,42 66,42" fill="currentColor" opacity="0.85" />
      <polygon points="104,6 112,18 96,18" fill="currentColor" opacity="0.85" />
      <path
        d="M 6 32 L 26 18 L 60 42 L 90 22 L 114 36"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="3 3"
        opacity="0.7"
      />
      <polygon points="58,38 70,42 58,46" fill="currentColor" />
    </svg>
  );
}

/**
 * Geometry-Dash-cube glyph: a ground line, a floor spike to clear and a
 * landable block, with the cube tracing a parabolic hop over the spike.
 * Reads as the one-button jumper at a glance — distinct from the Waves
 * corridor (no ceiling, solid square player rather than a triangle).
 */
function JumpArt() {
  return (
    <svg viewBox="0 0 120 60" className="mode-art-svg">
      <line x1="0" y1="48" x2="120" y2="48" stroke="currentColor" strokeWidth="2" opacity="0.55" />
      <polygon points="50,48 58,32 66,48" fill="currentColor" opacity="0.85" />
      <rect x="92" y="34" width="24" height="14" rx="2" fill="currentColor" opacity="0.4" />
      <path
        d="M 8 44 L 34 44 Q 58 6 82 44 L 92 34"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="3 3"
        opacity="0.7"
      />
      <rect x="12" y="38" width="11" height="11" rx="2" fill="currentColor" transform="rotate(-12 17 43)" />
    </svg>
  );
}

/**
 * The landing page. Shows six mode cards — Circles, Piano, Guitar,
 * Waves, Jump, and Pulse — and remembers the player's last choice so
 * they can re-enter with a single click. Per-mode tutorial gating
 * happens on each mode's home page, not here, so a brand-new player
 * still gets the picker (the front door) before being routed into the
 * tutorial of whichever mode they choose. The mode tiles flow
 * responsively (see `modePicker.css`).
 */
export function ModePickerPage() {
  const lastMode = loadModePref();

  return (
    <div className="mode-picker" data-testid="mode-picker">
      <section className="mode-picker-stage">
        <header className="mode-picker-hero">
          <h1 className="mode-picker-wordmark">
            BEAT<span style={{ color: 'var(--accent-cyan)' }}>HEAVEN</span>
            <span className="mode-picker-wordmark-sub">online rhythm games</span>
          </h1>
          {lastMode && (
            <p className="muted faint" data-testid="mode-picker-last">
              Last played: <strong>{modeLabel(lastMode)}</strong>
            </p>
          )}
        </header>

        <section className="mode-picker-grid">
          <ModeCard
            mode="circles"
            title="Circles"
            tagline="Ring tap"
            description="Aim, click, time the beat. osu-inspired circles charted to every track. Same infinite-mode loop progression as Pulse — just a different way to feel the song."
            controls="Click or tap on the circles"
            accentVar="var(--accent-magenta)"
            to="/circles"
          />
          <ModeCard
            mode="piano"
            title="Piano"
            tagline="Falling tiles"
            description="Four lanes of falling tiles. Tap them in time, build streaks, climb the cups. Speed ramps endlessly past cup three."
            controls="Four lane keys (rebindable) on keyboard, tap-on-lane on touch"
            accentVar="var(--accent-lime, #3bff9d)"
            to="/piano"
          />
          <ModeCard
            mode="guitar"
            title="Guitar"
            tagline="Neon fretboard"
            description="Perspective fretboard, four lanes, dense streams and sustains. Tighter timing and faster approach than Piano — the hardest mode in the game by design."
            controls="Four lane keys (rebindable) on keyboard, tap-on-lane on touch"
            accentVar="var(--accent-amber, #ffae3b)"
            to="/guitar"
          />
          <ModeCard
            mode="waves"
            title="Waves"
            tagline="Wave flight"
            description="Hold to climb, release to dive. Side-scrolling corridors built from the same NCS tracks Circles and Guitar use — every obstacle lands on a real beat. One touch ends the run."
            controls="Hold Space / W / J / ↑ (or click) to climb, release to dive"
            accentVar="var(--accent-cyan, #3bd8ff)"
            to="/waves"
          />
          <ModeCard
            mode="jump"
            title="Jump"
            tagline="Cube runner"
            description="Tap to jump in time with the music — clear spikes, land on blocks, hold to auto-hop. A one-button cube runner on the same NCS tracks as Circles and Waves, so every jump lands on a real beat. One touch ends the run."
            controls="Tap Space / W / J / ↑ (or click) to jump, hold to auto-hop"
            badge="NEW"
            accentVar="var(--accent-green, #3bff9d)"
            to="/jump"
          />
          <ModeCard
            mode="pulse"
            title="Pulse"
            tagline="One-button gravity runner"
            description="Flip gravity to the beat. Land on the pad. Stay on tempo. Pure rhythm runner with infinite-mode loops, speed ups, and stars/cups for every track."
            controls="Tap Space, click, or any key"
            accentVar="var(--accent-cyan)"
            to="/pulse"
          />
        </section>
      </section>

      <div className="mode-picker-content">
        <section className="mode-picker-intro" aria-labelledby="mode-picker-intro-h">
          <h2 id="mode-picker-intro-h">Free online rhythm games, right in your browser</h2>
          <p>
            BeatHeaven is a free browser rhythm game with six ways to play. Tap
            osu-inspired circles, catch Piano Tiles-inspired falling tiles, shred
            a Guitar Hero / Clone Hero-inspired fretboard, or fly and jump through
            Geometry-Dash-inspired courses — every mode is charted to free NCS
            music. There&apos;s nothing to download or install: it loads instantly
            and works unblocked at school or work.
          </p>
          <p>
            Want to dig into the soundtrack? Browse the{' '}
            <Link to="/songs">best NCS songs for gaming</Link>, or bring your own
            tracks with{' '}
            <Link to="/pulse/browse">community maps and custom songs</Link>.
          </p>
        </section>

        <section className="mode-picker-faq" aria-labelledby="mode-picker-faq-h">
          <h2 id="mode-picker-faq-h">Frequently asked questions</h2>
          <div className="mode-picker-faq-list">
            <article className="mode-picker-faq-item">
              <h3>Is BeatHeaven free?</h3>
              <p>
                Yes. Every mode and every song is completely free to play, with
                no sign-up needed to start.
              </p>
            </article>
            <article className="mode-picker-faq-item">
              <h3>Can I play unblocked at school?</h3>
              <p>
                BeatHeaven runs entirely in your browser with no download or
                install, so it works on locked-down school and work networks
                wherever the site itself is reachable.
              </p>
            </article>
            <article className="mode-picker-faq-item">
              <h3>Do I need to download anything?</h3>
              <p>
                No. It&apos;s a browser rhythm game — pick a mode and play
                instantly on desktop or mobile, with no app or install.
              </p>
            </article>
            <article className="mode-picker-faq-item">
              <h3>What rhythm games are included?</h3>
              <p>
                Six: an <Link to="/circles">osu-inspired circle clicker</Link>, a{' '}
                <Link to="/piano">Piano Tiles-inspired falling-tile mode</Link>, a{' '}
                <Link to="/guitar">Guitar Hero / Clone Hero-inspired fretboard</Link>, two
                Geometry-Dash-inspired modes (<Link to="/jump">Jump</Link> and{' '}
                <Link to="/waves">Waves</Link>), and a{' '}
                <Link to="/pulse">one-button gravity runner</Link>.
              </p>
            </article>
            <article className="mode-picker-faq-item">
              <h3>Can I play my own songs?</h3>
              <p>
                Yes — alongside the NCS catalogue you can play community-made
                maps with custom songs, and build your own in the map editor.
              </p>
            </article>
          </div>
        </section>

        <footer className="mode-picker-footer muted">
          <p>
            Universal pages: <Link to="/songs">NCS songs</Link> ·{' '}
            <Link to="/calibrate">Calibrate</Link> ·{' '}
            <Link to="/profile">Profile</Link>
          </p>
          <TrademarkDisclaimer />
        </footer>
      </div>
    </div>
  );
}
